/**
 * Scenario rate series virtual views — the canonical per-string
 * optimization-scenario surfaces: one row per `(string × run timestamp)`, each
 * scenario measure a TYPED scalar column carrying its unit.
 *
 * The string grain extends the shared `SCENARIO_BASE` with the full GAP setpoint
 * solve (`OPTIMIZATION_RESULT`) and, for the ad-hoc families, operator
 * annotations (`AD_HOC_RESULT`). Maps over the shared `SCENARIO_FAMILIES` SSOT
 * via `buildScenarioFamilyViews`, so each view's aligned measures resolve
 * (`prefix + suffix`) to real source timeseries columns on
 * `OptimizedRates_Network_String_Agent`, each typed via the unit metadata. The
 * two ad-hoc simulation views additionally classify a `scenarioType` enum from
 * the per-measure `comment` column.
 *
 * Built per session (not module-level) because the aligned factory closes over
 * the CDF client to source-read + datapoints-retrieve, and over the
 * hierarchy/entity views to resolve ancestry.
 */

import type { VirtualView } from '../createReshapedView.ts';
import { describeAncestry, describeStringScope, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import { AD_HOC_RESULT, OPTIMIZATION_RESULT, type Shape, STRING_KEEP } from '../shapes/index.ts';
import { buildScenarioFamilyViews, type ScenarioFamily, type ScenarioKind } from './optimizationScenarios.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import { OptimizedRatesNetworkStringAgentView } from '#schema/generated/ipso/views.ts';

/**
 * The string grain's per-kind measure shape: every family carries the full GAP
 * setpoint solve (`OPTIMIZATION_RESULT`); the ad-hoc families additionally carry
 * the operator annotations (`AD_HOC_RESULT`).
 */
export const STRING_SCENARIO_SHAPE_BY_KIND: Record<ScenarioKind, Shape> = {
  standard: OPTIMIZATION_RESULT,
  systemOpt: OPTIMIZATION_RESULT,
  adHocOpt: AD_HOC_RESULT,
  adHocSim: AD_HOC_RESULT,
};

/**
 * Build one timestamp-aligned rate surface per optimization scenario — the
 * canonical optimization-scenario views. Each is a `createAlignedSeriesView`
 * over the shared source whose measure handles align into one TYPED scalar
 * column per measure (units intact) plus a `timestamp` column, carrying identity
 * + the wellString relation and the well/field/reservoir ancestry on every
 * aligned row. Filter by wellString (or join WellEntity) to scope by hierarchy.
 *
 * The two ad-hoc simulation views additionally carry a `scenarioType` enum
 * classified from the per-measure `comment` column, so the agent can filter
 * those views by scenario type.
 */
export const useScenarioRateSeriesViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return buildScenarioFamilyViews({
    grainStem: '',
    source: OptimizedRatesNetworkStringAgentView,
    keep: STRING_KEEP,
    enrich: STRING_CONTEXT_ENRICH,
    shapeByKind: STRING_SCENARIO_SHAPE_BY_KIND,
    describe: (_family: ScenarioFamily, name: string) =>
      `Per-string ${name} aligned by run timestamp: one row per (string × timestamp), each measure a typed column${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    client,
    mintSeries,
  });
};
